import { Button } from '../Button';
import { SkeletonBlock } from '../SkeletonBlock';
import { SearchSmallIcon } from '../../icons/SearchSmallIcon';
import { radius } from '../../theme/tokens';
import {
  Actions,
  Content,
  EmptyCard,
  EmptyHeading,
  Field,
  FieldLabel,
  FieldValue,
  HeaderInfo,
  HeaderRow,
  Image,
  ImageGrid,
  ImagePlaceholder,
  ImageTile,
  ImageWrapper,
  ListActions,
  ListCard,
  ListFieldLabel,
  ListFieldValue,
  ListStatusPill,
  LoadingPillSlot,
  MetaField,
  MetaRow,
  PopulatedCard,
  StatusPill,
  TileImage,
  TilePlaceholder,
} from './AccountBookingCard.styles';
import type { AccountBookingCardProps } from './AccountBookingCard.types';

const LIST_MAX_IMAGES = 3;

export function AccountBookingCard(props: AccountBookingCardProps) {
  if (props.variant === 'loading') {
    const { loadingLabel = 'Loading booking' } = props;
    return (
      <PopulatedCard aria-busy="true" aria-label={loadingLabel}>
        <ImageWrapper>
          <SkeletonBlock width="100%" height="100%" />
        </ImageWrapper>
        <Content>
          <LoadingPillSlot>
            <SkeletonBlock
              width="80px"
              height="24px"
              borderRadius={radius('chip', 'radius-full')}
            />
          </LoadingPillSlot>
          <Field>
            <SkeletonBlock variant="text" width="30%" />
            <SkeletonBlock variant="text" width="70%" />
          </Field>
          <Field>
            <SkeletonBlock variant="text" width="40%" />
            <SkeletonBlock variant="text" width="50%" />
          </Field>
          <Actions>
            <SkeletonBlock variant="button" width="140px" />
          </Actions>
        </Content>
      </PopulatedCard>
    );
  }

  if (props.variant === 'empty') {
    const { message, ctaLabel, onCtaClick } = props;
    return (
      <EmptyCard aria-label={message}>
        <EmptyHeading>{message}</EmptyHeading>
        <Button type="button" variant="primary" onClick={onCtaClick}>
          <SearchSmallIcon size={14} />
          {ctaLabel}
        </Button>
      </EmptyCard>
    );
  }

  if (props.variant === 'list-loading') {
    const { labels } = props;
    return (
      <ListCard aria-busy="true" aria-label={labels.loading ?? 'Loading booking'}>
        <ImageGrid>
          {Array.from({ length: LIST_MAX_IMAGES }, (_, index) => (
            <ImageTile key={index}>
              <SkeletonBlock width="100%" height="100%" />
            </ImageTile>
          ))}
        </ImageGrid>
        <HeaderRow>
          <HeaderInfo>
            <SkeletonBlock variant="text" width="60%" />
            <SkeletonBlock variant="text" width="80%" />
          </HeaderInfo>
          <SkeletonBlock width="72px" height="20px" borderRadius={radius('chip', 'radius-full')} />
        </HeaderRow>
        <MetaRow>
          <MetaField>
            <SkeletonBlock variant="text" width="40%" />
            <SkeletonBlock variant="text" width="70%" />
          </MetaField>
          <MetaField>
            <SkeletonBlock variant="text" width="40%" />
            <SkeletonBlock variant="text" width="60%" />
          </MetaField>
        </MetaRow>
        <ListActions>
          <SkeletonBlock variant="button" width="100%" />
        </ListActions>
      </ListCard>
    );
  }

  if (props.variant === 'list') {
    const { images, reference, status, dates, total, labels, onViewBooking, onAddToBooking } =
      props;
    const tiles = (Array.isArray(images) && images.length > 0 ? images : [{}]).slice(
      0,
      LIST_MAX_IMAGES
    );

    return (
      <ListCard aria-label={`${labels.statuses[status]} — ${reference}`}>
        <ImageGrid>
          {tiles.map((image, index) => (
            <ImageTile key={index}>
              {image.url ? (
                <TileImage src={image.url} alt={image.alt ?? ''} />
              ) : (
                <TilePlaceholder>{labels.imagePlaceholder}</TilePlaceholder>
              )}
            </ImageTile>
          ))}
        </ImageGrid>

        <HeaderRow>
          <HeaderInfo>
            <ListFieldLabel>{labels.reference}</ListFieldLabel>
            <ListFieldValue>{reference}</ListFieldValue>
          </HeaderInfo>
          <ListStatusPill data-status={status}>{labels.statuses[status]}</ListStatusPill>
        </HeaderRow>

        <MetaRow>
          <MetaField>
            <ListFieldLabel>{labels.date}</ListFieldLabel>
            <ListFieldValue>{dates}</ListFieldValue>
          </MetaField>
          <MetaField>
            <ListFieldLabel>{labels.total}</ListFieldLabel>
            <ListFieldValue>{total}</ListFieldValue>
          </MetaField>
        </MetaRow>

        <ListActions>
          <Button type="button" variant="outline" onClick={onViewBooking}>
            {labels.view}
          </Button>
          {onAddToBooking && (
            <Button type="button" variant="primary" onClick={onAddToBooking}>
              {labels.addToBooking}
            </Button>
          )}
        </ListActions>
      </ListCard>
    );
  }

  const {
    imageUrl,
    imageAlt,
    imagePlaceholderLabel,
    status,
    statusLabels,
    dateLabel,
    dates,
    referenceLabel,
    reference,
    viewBookingLabel,
    onViewBooking,
  } = props;

  return (
    <PopulatedCard aria-label={`${statusLabels[status]} — ${reference}`}>
      <ImageWrapper>
        {imageUrl ? (
          <Image src={imageUrl} alt={imageAlt ?? ''} />
        ) : (
          <ImagePlaceholder>{imagePlaceholderLabel ?? 'No image'}</ImagePlaceholder>
        )}
      </ImageWrapper>
      <Content>
        <StatusPill data-status={status}>{statusLabels[status]}</StatusPill>
        <Field>
          <FieldLabel>{dateLabel}</FieldLabel>
          <FieldValue>{dates}</FieldValue>
        </Field>
        <Field>
          <FieldLabel>{referenceLabel}</FieldLabel>
          <FieldValue>{reference}</FieldValue>
        </Field>
        <Actions>
          <Button type="button" variant="primary" onClick={onViewBooking}>
            {viewBookingLabel}
          </Button>
        </Actions>
      </Content>
    </PopulatedCard>
  );
}
